import React, { useState, useEffect, useMemo } from 'react';
import {
  MapPinIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BuildingOffice2Icon,
  HomeIcon,
  BuildingStorefrontIcon,
  BoltIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChartBarIcon,
  ArrowPathIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../contexts/LanguageContext';
import CommonPageHeader from '../components/common/CommonPageHeader';
import LoadingScreen from '../components/common/LoadingScreen';
import meterReadingService from '../services/meterReadingService';
import { ConsumptionLocation, MeterPoint } from '../types/meterReading';
import ConsumptionLocationForm from '../components/meters/ConsumptionLocationForm';
import MeterPointsList from '../components/meters/MeterPointsList';
import AssignPodModal from '../components/meters/AssignPodModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useCustomers } from '../hooks/useCustomers';
import { Combobox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon, UserIcon } from '@heroicons/react/24/outline';

/**
 * Fogyasztási Helyek Kezelése
 * 
 * Ezen az oldalon lehet:
 * - Fogyasztási helyeket létrehozni/szerkeszteni
 * - POD-okat hozzárendelni fogyasztási helyekhez
 * - Mérőhelyeket kezelni
 */
const ConsumptionLocations: React.FC = () => {
  const { t } = useLanguage();
  const { customers } = useCustomers();
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState<ConsumptionLocation[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<ConsumptionLocation | null>(null);
  const [meterPoints, setMeterPoints] = useState<MeterPoint[]>([]);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [showAssignPodModal, setShowAssignPodModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState<ConsumptionLocation | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [customerQuery, setCustomerQuery] = useState('');
  
  // Confirmation dialogs
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });
  
  // Szűrők és keresés
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [locationTypeFilter, setLocationTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showAnalytics, setShowAnalytics] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // TODO: Ügyfél kiválasztás után betöltjük az adatokat
      // Egyelőre üres lista
      setLocations([]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLocationsForCustomer = async (customerId: string) => {
    try {
      setLoading(true);
      const customerLocations = await meterReadingService.getConsumptionLocations(customerId);
      setLocations(customerLocations);
      setSelectedCustomerId(customerId);
    } catch (error) {
      console.error('Error loading locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerSelect = async (customer: any) => {
    setSelectedCustomer(customer);
    if (customer) {
      setSelectedCustomerId(customer.id);
      await loadLocationsForCustomer(customer.id);
    } else {
      setLocations([]);
      setSelectedCustomerId('');
      setSelectedLocation(null);
    }
  };

  const filteredCustomers = useMemo(() => {
    if (!customerQuery) return customers;
    return customers.filter((customer) => {
      const searchStr = customerQuery.toLowerCase();
      const fullName = `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
      return (
        fullName.toLowerCase().includes(searchStr) ||
        customer.companyName?.toLowerCase().includes(searchStr) ||
        customer.taxNumber?.toLowerCase().includes(searchStr)
      );
    });
  }, [customers, customerQuery]);

  const loadMeterPointsForLocation = async (locationId: string) => {
    try {
      console.log('📍 Mérőhelyek betöltése location ID-hoz:', locationId);
      const points = await meterReadingService.getMeterPointsByLocation(locationId);
      console.log('✅ Betöltött mérőhelyek:', points.length);
      setMeterPoints(points);
    } catch (error) {
      console.error('Error loading meter points:', error);
    }
  };

  const handleLocationSelect = async (location: ConsumptionLocation) => {
    setSelectedLocation(location);
    await loadMeterPointsForLocation(location.id);
  };

  const handleCreateLocation = () => {
    setEditingLocation(null);
    setShowLocationForm(true);
  };

  const handleEditLocation = (location: ConsumptionLocation) => {
    setEditingLocation(location);
    setShowLocationForm(true);
  };

  const handleLocationSaved = async () => {
    setShowLocationForm(false);
    setEditingLocation(null);
    if (selectedCustomerId) {
      await loadLocationsForCustomer(selectedCustomerId);
    }
  };

  const handleAssignPod = () => {
    if (selectedLocation) {
      setShowAssignPodModal(true);
    }
  };

  const handlePodAssigned = async () => {
    console.log('🔄 POD hozzárendelve, lista frissítése...');
    setShowAssignPodModal(false);
    if (selectedLocation) {
      await loadMeterPointsForLocation(selectedLocation.id);
      console.log('✅ Mérőhelyek lista frissítve');
    }
  };

  const handleRemovePod = (meterPoint: MeterPoint) => {
    setConfirmDialog({
      isOpen: true,
      title: 'POD eltávolítása',
      message: `Biztosan eltávolítod ezt a POD-ot?\n\n${meterPoint.podId}\n\nEz a művelet nem vonható vissza!`,
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        
        try {
          console.log('🗑️ POD eltávolítása:', meterPoint.podId);
          await meterReadingService.removeMeterPoint(meterPoint.id);
          
          // Frissítjük a listát
          if (selectedLocation) {
            await loadMeterPointsForLocation(selectedLocation.id);
          }
          
          console.log('✅ POD sikeresen eltávolítva');
        } catch (error) {
          console.error('❌ POD eltávolítási hiba:', error);
          setConfirmDialog({
            isOpen: true,
            title: 'Hiba',
            message: 'Hiba történt a POD eltávolítása során',
            onConfirm: () => setConfirmDialog({ ...confirmDialog, isOpen: false })
          });
        }
      }
    });
  };

  const handleDeleteLocation = (location: ConsumptionLocation) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Fogyasztási hely törlése',
      message: `Biztosan törlöd ezt a fogyasztási helyet?\n\n${location.locationName}\n${location.fullAddress || ''}\n\nEz a művelet nem vonható vissza!`,
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        
        try {
          console.log('🗑️ Fogyasztási hely törlése:', location.locationName);
          await meterReadingService.deleteConsumptionLocation(location.id);
          
          // Ha ez volt kiválasztva, töröljük a kijelölést
          if (selectedLocation?.id === location.id) {
            setSelectedLocation(null);
            setMeterPoints([]);
          }
          
          // Frissítjük a listát
          if (selectedCustomerId) {
            await loadLocationsForCustomer(selectedCustomerId);
          }
          
          console.log('✅ Fogyasztási hely sikeresen törölve');
        } catch (error: any) {
          console.error('❌ Fogyasztási hely törlési hiba:', error);
          setConfirmDialog({
            isOpen: true,
            title: 'Hiba',
            message: error.message || 'Hiba történt a fogyasztási hely törlése során',
            onConfirm: () => setConfirmDialog({ ...confirmDialog, isOpen: false })
          });
        }
      }
    });
  };

  const getLocationTypeIcon = (type: string) => {
    switch (type) {
      case 'residential':
        return <HomeIcon className="h-5 w-5" />;
      case 'commercial':
        return <BuildingStorefrontIcon className="h-5 w-5" />;
      case 'industrial':
        return <BuildingOffice2Icon className="h-5 w-5" />;
      default:
        return <MapPinIcon className="h-5 w-5" />;
    }
  };

  const getLocationTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      residential: 'Lakossági',
      commercial: 'Kereskedelmi',
      industrial: 'Ipari'
    };
    return labels[type] || type;
  };

  // Szűrt és keresett helyek
  const filteredLocations = useMemo(() => {
    return locations.filter(location => {
      const searchMatch = !searchTerm || 
        location.locationName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (location.fullAddress && location.fullAddress.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const typeMatch = !locationTypeFilter || location.locationType === locationTypeFilter;
      const statusMatch = !statusFilter || 
        (statusFilter === 'active' && location.isActive) ||
        (statusFilter === 'inactive' && !location.isActive);
      
      return searchMatch && typeMatch && statusMatch;
    });
  }, [locations, searchTerm, locationTypeFilter, statusFilter]);

  // Statisztikák
  const analytics = useMemo(() => {
    return {
      total: locations.length,
      active: locations.filter(l => l.isActive).length,
      inactive: locations.filter(l => !l.isActive).length,
      residential: locations.filter(l => l.locationType === 'residential').length,
      commercial: locations.filter(l => l.locationType === 'commercial').length,
      industrial: locations.filter(l => l.locationType === 'industrial').length,
      totalMeterPoints: meterPoints.length
    };
  }, [locations, meterPoints]);

  // Pagination
  const paginatedLocations = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredLocations.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredLocations, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredLocations.length / itemsPerPage);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <CommonPageHeader
        title="Fogyasztási Helyek"
        subtitle="Fogyasztási helyek és mérőhelyek (POD-ok) kezelése"
        icon={MapPinIcon}
      />

      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {/* Ügyfél választó */}
        <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              <UserIcon className="h-8 w-8 text-blue-500" />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Ügyfél kiválasztása
              </label>
              <Combobox value={selectedCustomer} onChange={handleCustomerSelect}>
                <div className="relative">
                  <Combobox.Input
                    className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 pr-10"
                    displayValue={(customer: any) => {
                      if (!customer) return '';
                      if (customer.companyName) return customer.companyName;
                      return `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 'Névtelen ügyfél';
                    }}
                    onChange={(event) => setCustomerQuery(event.target.value)}
                    placeholder="Kezdj el gépelni az ügyfél kereséséhez..."
                  />
                  <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </Combobox.Button>
                  <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                    {filteredCustomers.length === 0 && customerQuery !== '' ? (
                      <div className="relative cursor-default select-none py-2 px-4 text-gray-700 dark:text-gray-300">
                        Nincs találat
                      </div>
                    ) : (
                      filteredCustomers.map((customer) => (
                        <Combobox.Option
                          key={customer.id}
                          value={customer}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                              active ? 'bg-blue-600 text-white' : 'text-gray-900 dark:text-gray-100'
                            }`
                          }
                        >
                          {({ selected, active }) => {
                            const displayName = customer.companyName || 
                              `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || 
                              'Névtelen ügyfél';
                            return (
                            <>
                              <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                {displayName}
                                {customer.taxNumber && (
                                  <span className="ml-2 text-sm opacity-75">({customer.taxNumber})</span>
                                )}
                              </span>
                              {selected && (
                                <span
                                  className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                    active ? 'text-white' : 'text-blue-600'
                                  }`}
                                >
                                  <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                </span>
                              )}
                            </>
                          );
                          }}
                        </Combobox.Option>
                      ))
                    )}
                  </Combobox.Options>
                </div>
              </Combobox>
            </div>
          </div>
          {selectedCustomer && (
            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <span className="font-medium">Kiválasztott ügyfél:</span>{' '}
                {selectedCustomer.companyName || `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim()}
                {selectedCustomer.taxNumber && (
                  <span className="ml-2">• Adószám: {selectedCustomer.taxNumber}</span>
                )}
              </p>
            </div>
          )}
        </div>

        {/* Statisztikák */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Összes hely</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{analytics.total}</p>
              </div>
              <MapPinIcon className="h-10 w-10 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Aktív helyek</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-1">{analytics.active}</p>
              </div>
              <CheckCircleIcon className="h-10 w-10 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Inaktív helyek</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">{analytics.inactive}</p>
              </div>
              <XCircleIcon className="h-10 w-10 text-red-500" />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Mérőhelyek</p>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">{analytics.totalMeterPoints}</p>
              </div>
              <BoltIcon className="h-10 w-10 text-purple-500" />
            </div>
          </div>
        </div>

        {/* Keresés és szűrők */}
        <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Keresés */}
              <div className="flex-1 relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Keresés név vagy cím alapján..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              
              {/* Szűrők gomb */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                <FunnelIcon className="h-5 w-5 mr-2" />
                Szűrők
                {showFilters ? <ChevronUpIcon className="h-4 w-4 ml-2" /> : <ChevronDownIcon className="h-4 w-4 ml-2" />}
              </button>
              
              {/* Statisztikák gomb */}
              <button
                onClick={() => setShowAnalytics(!showAnalytics)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                <ChartBarIcon className="h-5 w-5 mr-2" />
                Részletek
              </button>
              
              {/* Frissítés */}
              <button
                onClick={loadData}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                <ArrowPathIcon className="h-5 w-5" />
              </button>
              
              {/* Új hely */}
              <button
                onClick={handleCreateLocation}
                disabled={!selectedCustomerId}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Új hely
              </button>
            </div>
            
            {/* Szűrő panel */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Típus
                  </label>
                  <select
                    value={locationTypeFilter}
                    onChange={(e) => setLocationTypeFilter(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Összes típus</option>
                    <option value="residential">Lakossági</option>
                    <option value="commercial">Kereskedelmi</option>
                    <option value="industrial">Ipari</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Státusz
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Összes státusz</option>
                    <option value="active">Aktív</option>
                    <option value="inactive">Inaktív</option>
                  </select>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setLocationTypeFilter('');
                      setStatusFilter('');
                      setSearchTerm('');
                    }}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Szűrők törlése
                  </button>
                </div>
              </div>
            )}
            
            {/* Részletes statisztikák */}
            {showAnalytics && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Típus szerinti megoszlás</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <HomeIcon className="h-8 w-8 text-blue-500" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{analytics.residential}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Lakossági</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <BuildingStorefrontIcon className="h-8 w-8 text-green-500" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{analytics.commercial}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Kereskedelmi</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-2">
                      <BuildingOffice2Icon className="h-8 w-8 text-purple-500" />
                    </div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{analytics.industrial}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Ipari</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Fogyasztási helyek lista */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      Fogyasztási helyek
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {filteredLocations.length} / {locations.length} hely
                    </p>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Oldal {currentPage} / {totalPages}
                  </div>
                </div>
              </div>

              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {paginatedLocations.length === 0 ? (
                  <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                    <MapPinIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Nincs fogyasztási hely</p>
                    <p className="text-sm mt-1">Válassz ki egy ügyfelet és hozz létre egyet</p>
                  </div>
                ) : (
                  paginatedLocations.map((location) => (
                    <div
                      key={location.id}
                      onClick={() => handleLocationSelect(location)}
                      className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                        selectedLocation?.id === location.id
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500'
                          : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          <div className="flex-shrink-0 mt-1">
                            {getLocationTypeIcon(location.locationType)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {location.locationName}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              {location.fullAddress || `${location.address.postalCode} ${location.address.city}, ${location.address.street} ${location.address.houseNumber}`}
                            </p>
                            <div className="flex items-center mt-2">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                                {getLocationTypeLabel(location.locationType)}
                              </span>
                              {!location.isActive && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200">
                                  Inaktív
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1 ml-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditLocation(location);
                            }}
                            className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                            title="Szerkesztés"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteLocation(location);
                            }}
                            className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                            title="Törlés"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Előző
                    </button>
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Következő
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Mérőhelyek (POD-ok) */}
          <div className="lg:col-span-2">
            {selectedLocation ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        Mérőhelyek (POD-ok)
                      </h3>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {selectedLocation.locationName}
                      </p>
                    </div>
                    <button
                      onClick={handleAssignPod}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      <BoltIcon className="h-5 w-5 mr-2" />
                      POD hozzárendelése
                    </button>
                  </div>
                </div>

                <MeterPointsList
                  meterPoints={meterPoints}
                  onRefresh={() => loadMeterPointsForLocation(selectedLocation.id)}
                  onRemove={handleRemovePod}
                />
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center">
                <MapPinIcon className="h-16 w-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Válassz ki egy fogyasztási helyet
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  A bal oldali listából válassz ki egy fogyasztási helyet a mérőhelyek megtekintéséhez
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fogyasztási hely form modal */}
      {showLocationForm && (
        <ConsumptionLocationForm
          customerId={selectedCustomerId}
          location={editingLocation}
          onClose={() => setShowLocationForm(false)}
          onSaved={handleLocationSaved}
        />
      )}

      {/* POD hozzárendelés modal */}
      {showAssignPodModal && selectedLocation && (
        <AssignPodModal
          consumptionLocationId={selectedLocation.id}
          customerId={selectedCustomerId}
          onClose={() => setShowAssignPodModal(false)}
          onAssigned={handlePodAssigned}
        />
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Törlés"
        cancelText="Mégse"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        icon="danger"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />
    </div>
  );
};

export default ConsumptionLocations;
